import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getAccessibilityFeaturesList } from "../../../../../queries/queryFunctions/publicQueryFns/getQueryFns";
import { usePublicContext } from "../../../../../providers/ReactPublicContextProvider";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import SlideOver from "../../../../layouts/SlideOver";
import ErrorFormik from "../../../ui/ErrorFormik";
import CustomInput from "../../../ui/custom-inputs/CustomInput";
import Label from "../../../ui/custom-inputs/Label";
import { postAddAccessibilityFeaturesQueryFn } from "../../../../../queries/queryFunctions/privetQueryFns/postQueryFns";
import { addAccessibilityFeaturesSchema } from "../../../../../utils/forms-schemas";
import DropdownBlog from "../../../ui/DropdownBlog";
import { patchAccessibilityFeatureQueryFn } from "../../../../../queries/queryFunctions/privetQueryFns/patchQueryFns";
import { deleteAccessibilityFeaturesQueryFn } from "../../../../../queries/queryFunctions/privetQueryFns/deleteQueryFns";
import Pagination from "../../../ui/Pagination";
import CustomModal from "../../../ui/Modal";

const AccessibilityFeaturesComponent = () => {
  const { setIsLoading } = usePublicContext();
  const [activePage, setActivePage] = useState(0);
  const [isSlideOverOpen, setIsSlideOverOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [featureIdToDelete, setFeatureIdToDelete] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [limit, setLimit] = useState(10);

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    isValid,
    resetForm,
  } = useFormik({
    initialValues: {
      nameEn: "",
      // description: "",
      nameAr: "",
    },
    validationSchema: addAccessibilityFeaturesSchema,
    onSubmit: onSubmit,
  });

  const {
    data: accessibilityData,
    isPending: isLoadingAccessibilityData,
    isError: isErrorAccessibilityData,
    refetch,
  } = useQuery({
    queryKey: ["accessibility-features", limit],
    queryFn: getAccessibilityFeaturesList,
  });

  const {
    mutate: addMutation,
    isPending: isLoadingMutate,
    isError: isErrorMutate,
  } = useMutation({
    mutationFn: postAddAccessibilityFeaturesQueryFn,
    onSuccess(data) {
      toast.success("Accessibility Feature Added Successfully");
      refetch();
    },
    onError(error) {
      toast.error("Something went wrong, please try later!");
    },
  });

  useEffect(() => {
    setIsLoading(isLoadingAccessibilityData);
  }, [isLoadingAccessibilityData]);

  const {
    mutate: updateMutation,
    isPending: isLoadingUpdateMutation,
    isError: isErrorUpdateMutation,
  } = useMutation({
    mutationFn: patchAccessibilityFeatureQueryFn,
    onSuccess(data) {
      toast.success("Feature Updated Successfully");
      refetch();
      // setIsSlideOverOpen(false);
    },
    onError(error) {
      toast.error("Something went wrong, please try later!");
    },
  });

  const {
    mutate: deleteMutation,
    isPending: isLoadingDeleteMutation,
    isError: isErrorDeleteMutation,
  } = useMutation({
    mutationFn: deleteAccessibilityFeaturesQueryFn,
    onSuccess(data) {
      toast.success("Feature Deleted Successfully");
      refetch();
      setShowAlert(false);
    },
    onError(error) {
      toast.error("Something went wrong, please try later!");
    },
  });

  function onSubmit() {
    if (selectedItem) {
      updateMutation({
        id: selectedItem?._id,
        nameEn: values.nameEn,
        nameAr: values.nameAr,
      });
    } else {
      addMutation({
        nameEn: values.nameEn,
        nameAr: values.nameAr,
      });
    
    }
    resetForm();
    setIsSlideOverOpen(false);
  }

  const openSlideOver = () => {
    setIsSlideOverOpen(true);
  };

  const closeSlideOver = () => {
    setSelectedItem(null);
    setFieldValue("nameEn", "");
    setFieldValue("nameAr", "");
    setIsSlideOverOpen(false);
  };

  const handleDelete = (id) => {
    setFeatureIdToDelete(id);
    setShowAlert(true);
  };

  const handleConfirmDelete = () => {
    deleteMutation(featureIdToDelete);
    setShowAlert(false);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFieldValue("nameEn", item.nameEn);
    setFieldValue("nameAr", item.nameAr);
    setIsSlideOverOpen(true);
  };

  const handleClick = (pageIndex) => {
    setActivePage(pageIndex);
  };

  const startIndex = activePage * pageSize;
  const endIndex = Math.min(startIndex + pageSize, accessibilityData?.length);

  const handleChangePageSize = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setPageSize(newSize);
    setLimit(newSize);
  };

  return (
    <div className='table-responsive'>
      <CustomModal
        title='Warning!'
        description='Deleting this type will remove it from the associated records.'
        isOpen={showAlert}
        setIsOpen={setShowAlert}
        buttonConfirmAction={handleConfirmDelete}
        buttonConfirmText={"Confirm Delete"}
        buttonCloseText='Cancel'
      />
      <div
        className='guest-calendar'
        style={{ display: "flex", justifyContent: "flex-end" }}
      >
        <Button className='me-2' variant='secondary' onClick={openSlideOver}>
          Add
        </Button>
      </div>

      <table className='table card-table display mb-4 shadow-hover default-table .dataTablesCard dataTable no-footer'>
        <thead>
          <tr>
            <th className='sorting_asc'>English Name</th>
            <th className='sorting'>Arabic Name</th>
            <th className='sorting bg-none'></th>
          </tr>
        </thead>
        <tbody>
          {accessibilityData &&
            accessibilityData
              .slice(startIndex, endIndex)
              .map((accessibilityFeature, index) => (
                <tr key={index} className={index % 2 === 0 ? "even" : "odd"}>
                  <td>
                    <div>
                      <h5 className='text-nowrap'>
                        {accessibilityFeature.nameEn}
                      </h5>
                    </div>
                  </td>
                  <td>
                    <div>
                      <h5 className='text-nowrap'>
                        {accessibilityFeature.nameAr}
                      </h5>
                    </div>
                  </td>
                  <DropdownBlog
                    showArchiveOption={false}
                    showDeleteOption={true}
                    showEditOption={true}
                    showLinkOption={false}
                    showPublishOption={false}
                    accessibilityFeatureId={accessibilityFeature?._id}
                    handleDelete={() => handleDelete(accessibilityFeature?._id)}
                    handleEdit={() => handleEdit(accessibilityFeature)}
                  />
                </tr>
              ))}
        </tbody>
      </table>

      <Pagination
        startIdx={startIndex}
        endIdx={endIndex}
        pageSize={pageSize}
        activePage={activePage}
        handleClick={handleClick}
        handleChangePageSize={handleChangePageSize}
        data={accessibilityData}
        itemsPerPage={pageSize}
        totalCount={accessibilityData?.length}
      />

      <SlideOver
        isOpen={isSlideOverOpen}
        onClose={closeSlideOver}
        onSubmit={handleSubmit}
      >
        <div>
          <h4>{selectedItem ? "Edit" : "Add"} Accessibility Features</h4>
          <section className='row'>
            <div className='col-lg-6'>
              <div className='form-group mb-3'>
                <Label forId='placeType' text='Name' isRequired={true} />
                <CustomInput
                  withFocus={true}
                  placeholder='Name'
                  id='nameEn'
                  name='nameEn'
                  type='text'
                  value={values?.nameEn}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <ErrorFormik
                  isError={errors?.nameEn}
                  isTouched={touched?.nameEn}
                  error={errors?.nameEn}
                />
              </div>
            </div>
            <div className='col-lg-6'>
              <div className='form-group mb-3'>
                <Label forId='nameAr' text='الاسم ' isRequired={true} />
                <CustomInput
                  withFocus={true}
                  placeholder='الاسم'
                  id='nameAr'
                  name='nameAr'
                  type='text'
                  value={values?.nameAr}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <ErrorFormik
                  isError={errors?.nameAr}
                  isTouched={touched?.nameAr}
                  error={errors?.nameAr}
                />
              </div>
            </div>
          </section>
        </div>
      </SlideOver>
    </div>
  );
};

export default AccessibilityFeaturesComponent;
